/*
 * duo-fidget-arcade.js  —  Duo Gift Arcade: Fidget Match-3 (gift discovery)
 * ---------------------------------------------------------------------------
 * The second arcade game. Where the door game co-designs a sign, THIS game
 * discovers a gift: the gems are real fidget packaging cards, and the fidgets
 * you reach for most reveal an interest, which we turn into a gift pick made
 * from the WHOLE catalog (including fidgets that were never on your board).
 *
 *   intro  ->  match-3 round (live collection strip)  ->  gift picks  ->  cart
 *
 * Reuse, not reinvention:
 *   - board mechanics: the gem-agnostic engine duo-arcade-engine.js (createGame
 *     with a fidget gemPool; applyMove/settle/autoPlay are color-blind to what a
 *     "gem" is).
 *   - catalog + recommender: duo-fidget-catalog.js + duo-fidget-recommender.js
 *     (pure, also Node-tested).
 *   - cart: standard Shopify /cart/add.js by variant id (a fidget is a stock
 *     product, so no configurator / pricing proxy is needed).
 *
 * Accessibility: each gem is a distinct illustrated card with an aria-label
 * (identity never rests on color alone), full keyboard play, reduced-motion
 * path, aria-live score + announcements.
 *
 * Test hooks: window.__duoFidgetArcade exposes the controller so a headless
 * harness can run a scripted, seeded round and screenshot play + picks.
 */
(function () {
  'use strict';

  var E = window.DuoArcadeEngine;
  var CAT = window.DuoFidgetCatalog;
  var REC = window.DuoFidgetRecommender;
  var ICONS = window.DuoFidgetIcons;
  if (!E || !CAT || !REC || !ICONS) { console.warn('[duo-fidget-arcade] engine/catalog/recommender/icons missing'); return; }

  var ROUND_SECONDS = 45;
  var TIME_CAP = 60;   // earned time bonuses can never push the clock past this
  // Motion preference: the visitor's explicit quick-settings choice
  // (html[data-motion]) when they made one, otherwise their OS setting.
  // REDUCED snaps the board straight to its final state, so honouring the
  // in-page switch here means "animations off" calms the arcade too.
  var REDUCED = (function () {
    var attr = document.documentElement.dataset.motion;
    if (attr === 'reduced') return true;
    if (attr === 'full') return false;
    return (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) || false;
  })();

  // Animation timing (ms). One cascade "step" = clear burst, then tiles fall +
  // new tiles drop in. Kept tight so a long chain still resolves in ~1s and the
  // board never feels locked. reduced-motion snaps straight to the final board.
  var CLEAR_MS = 150;   // burst hold before the board collapses
  var FALL_MS = 190;    // fall + spawn slide-in
  var STEP_GAP = 30;    // breath between cascade steps
  var SWAP_MS = 130;    // the two-tile swap slide

  // Human label for a power-up, for aria + tooltips.
  var SPECIAL_LABEL = {
    'line-h': 'row-clear power-up', 'line-v': 'column-clear power-up',
    'bomb': 'bomb power-up', 'rainbow': 'rainbow color bomb'
  };

  function track(name, data) {
    var payload = Object.assign({ event: 'duo_fidget_' + name }, data || {});
    try { (window.dataLayer = window.dataLayer || []).push(payload); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('duo:fidget:event', { detail: { name: name, data: data || {} } })); } catch (e2) {}
  }

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  // Haptic tap where supported (Android Chrome); silent no-op elsewhere.
  // Skipped under reduced motion alongside the visual juice.
  function buzz(ms) {
    if (REDUCED) return;
    try { if (navigator.vibrate && document.visibilityState === 'visible') navigator.vibrate(ms); } catch (e) {}
  }
  function money(price) {
    var n = parseFloat(price);
    return isNaN(n) ? '' : '$' + n.toFixed(2);
  }

  /* ---------- retention persistence (Lane E) ----------
   * All guest-first localStorage, never keyed on a customer id. Daily seeds +
   * streaks use the LOCAL calendar day so everything rolls at the player's
   * midnight, not UTC. */
  var BEST_KEY = 'duoFidgetBest';     // all-time best score
  var DAYS_KEY = 'duoFidgetDays';     // {last:'YYYY-MM-DD', count:N} played-days streak
  var DAILY_KEY = 'duoFidgetDaily';   // {day, best, sLast, sCount} daily-board best + streak
  // 1-3 stars at fixed score bands, aligned to the reward bands (gold opens at
  // 200; platinum at 1100). The empty third star is the honest replay hook.
  // Star bands track the reward bands (rewards.js DEFAULT_THRESHOLDS.fidget):
  // star 1 = gold (free shipping) 120, star 3 = platinum 400, star 2 midway.
  // Retuned 2026-07-07 with the v3-engine simulation (was 200/550/1100).
  var STAR_BANDS = [120, 260, 400];

  function localDayStr(d) {
    d = d || new Date();
    var m = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }
  function readJson(key) {
    try { var v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch (e) { return null; }
  }
  function writeJson(key, obj) {
    try { if (window.localStorage) localStorage.setItem(key, JSON.stringify(obj)); } catch (e) {}
  }

  /* ---------- sound: a tiny WebAudio synth (no audio files, no network) -----
   * One AudioContext, lazily created on the first real user gesture (the Start
   * tap or a board touch) so browser autoplay rules are honored and headless /
   * engine-driven test runs stay silent. Each event is a short synthesized
   * blip; nothing here touches the deterministic engine. Default ON with a
   * persistent mute toggle (localStorage). Math.random() is fine here, this is
   * cosmetic and never part of replay.
   * Mixer: master gain -> compressor -> destination. The compressor is what
   * lets the master sit hot (0.55): a deep cascade can stack half a dozen
   * voices, and without it they would sum past full scale and crackle. */
  var SoundFx = (function () {
    var ctx = null, master = null, comp = null, muted = false;
    try { muted = !!(window.localStorage && localStorage.getItem('duoFidgetMuted') === '1'); } catch (e) {}
    function ensure() {
      if (ctx) return ctx;
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      try {
        ctx = new AC();
        master = ctx.createGain();
        master.gain.value = 0.55;
        var out = ctx.destination;
        try {
          comp = ctx.createDynamicsCompressor();
          comp.threshold.value = -18;
          comp.knee.value = 12;
          comp.ratio.value = 5;
          comp.attack.value = 0.003;
          comp.release.value = 0.25;
          comp.connect(ctx.destination);
          out = comp;
        } catch (e2) { comp = null; }
        master.connect(out);
      } catch (e) { ctx = null; }
      return ctx;
    }
    function tone(o) {
      if (muted || !ctx) return;
      try {
        var t0 = ctx.currentTime + (o.delay || 0);
        var osc = ctx.createOscillator();
        var g = ctx.createGain();
        osc.type = o.type || 'triangle';
        osc.frequency.setValueAtTime(o.freq, t0);
        if (o.to && o.to !== o.freq) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.to), t0 + o.dur);
        var vol = (o.vol == null ? 0.5 : o.vol);
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(vol, t0 + 0.008);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);
        osc.connect(g); g.connect(master);
        osc.start(t0); osc.stop(t0 + o.dur + 0.02);
      } catch (e) {}
    }
    function noise(o) {
      if (muted || !ctx) return;
      try {
        var t0 = ctx.currentTime + (o.delay || 0);
        var dur = o.dur || 0.2;
        var n = Math.max(1, Math.floor(ctx.sampleRate * dur));
        var buf = ctx.createBuffer(1, n, ctx.sampleRate);
        var d = buf.getChannelData(0);
        for (var i = 0; i < n; i += 1) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
        var src = ctx.createBufferSource(); src.buffer = buf;
        var lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = o.cut || 700;
        var g = ctx.createGain(); g.gain.value = (o.vol == null ? 0.4 : o.vol);
        src.connect(lp); lp.connect(g); g.connect(master);
        src.start(t0); src.stop(t0 + dur);
      } catch (e) {}
    }
    return {
      unlock: function () {
        ensure();
        // iOS Safari can hand back a context that is 'suspended' (or
        // 'interrupted' after a call/Siri); any real gesture may resume it.
        if (ctx && ctx.state !== 'running') {
          try { var p = ctx.resume(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
        }
      },
      isMuted: function () { return muted; },
      setMuted: function (m) { muted = !!m; try { localStorage.setItem('duoFidgetMuted', m ? '1' : '0'); } catch (e) {} },
      // QA introspection only: graph shape plus live compressor reduction
      // (dips negative while sound is actually flowing through the bus).
      state: function () {
        return { ctx: ctx ? ctx.state : 'none', master: master ? master.gain.value : 0, compressor: !!comp, reduction: comp ? comp.reduction : 0, muted: muted };
      },
      ui: function () { tone({ freq: 520, to: 720, dur: 0.08, type: 'triangle', vol: 0.4 }); },
      move: function () { tone({ freq: 300, to: 360, dur: 0.06, type: 'triangle', vol: 0.24 }); },
      invalid: function () { tone({ freq: 165, to: 110, dur: 0.16, type: 'sawtooth', vol: 0.24 }); },
      // clear pitch climbs +2 semitones per cascade chain, capped at +12 (one
      // octave), with a little extra weight behind the deeper chains.
      clear: function (chain) {
        var lift = Math.min(12, Math.max(0, ((chain || 1) - 1) * 2));
        var base = 415 * Math.pow(2, lift / 12);
        tone({ freq: base, to: base * 1.5, dur: 0.12, type: 'triangle', vol: Math.min(0.72, 0.5 + lift * 0.018) });
      },
      mint: function (type) {
        if (type === 'rainbow') {
          [0, 1, 2, 3].forEach(function (i) { tone({ freq: 520 * Math.pow(1.18, i), dur: 0.1, type: 'square', vol: 0.26, delay: i * 0.05 }); });
        } else {
          tone({ freq: 600, to: 920, dur: 0.1, type: 'square', vol: 0.3 });
          tone({ freq: 920, dur: 0.08, type: 'square', vol: 0.26, delay: 0.06 });
        }
      },
      activate: function (effect) {
        if (effect === 'bomb') { noise({ dur: 0.3, vol: 0.5, cut: 480 }); tone({ freq: 120, to: 55, dur: 0.3, type: 'sine', vol: 0.5 }); }
        else if (effect === 'rainbow' || effect === 'combo' || effect === 'finale') { [0, 1, 2, 3, 4].forEach(function (i) { tone({ freq: 440 * Math.pow(1.2, i), dur: 0.14, type: 'triangle', vol: 0.26, delay: i * 0.04 }); }); }
        else { tone({ freq: 920, to: 280, dur: 0.18, type: 'sawtooth', vol: 0.3 }); } // line zip
      },
      // the gift reveal: a rising major arpeggio with a sparkle riding the top
      reveal: function () {
        [392, 523, 659, 784, 1047, 1319].forEach(function (f, i) { tone({ freq: f, dur: 0.2, type: 'triangle', vol: 0.38, delay: i * 0.07 }); });
        tone({ freq: 1568, to: 2093, dur: 0.32, type: 'sine', vol: 0.2, delay: 0.44 });
        noise({ dur: 0.3, vol: 0.1, cut: 2600, delay: 0.42 });
      },
      // time-up flourish: low boom, fast climbing run, then a held bright
      // chord. The detonations that follow layer their own activate() voices
      // on top; the compressor keeps the sum clean.
      finale: function () {
        noise({ dur: 0.45, vol: 0.5, cut: 420 });
        tone({ freq: 98, to: 49, dur: 0.5, type: 'sine', vol: 0.55 });
        [262, 330, 392, 523, 659, 784, 1047, 1319].forEach(function (f, i) { tone({ freq: f, dur: 0.11, type: 'square', vol: 0.22, delay: 0.16 + i * 0.055 }); });
        [523, 659, 784, 1047].forEach(function (f) { tone({ freq: f, dur: 0.55, type: 'triangle', vol: 0.28, delay: 0.64 }); });
        noise({ dur: 0.35, vol: 0.12, cut: 2600, delay: 0.64 });
      },
      // a soft clock tick for the last 10 seconds; deliberately far below clear
      tick: function () { tone({ freq: 1180, dur: 0.03, type: 'square', vol: 0.07 }); },
      // a quick riffle when the board reshuffles itself
      shuffle: function () { [300, 380, 320, 440, 360, 520].forEach(function (f, i) { tone({ freq: f, dur: 0.06, type: 'triangle', vol: 0.22, delay: i * 0.035 }); }); }
    };
  })();

  /* ===================================================================== */

  function FidgetArcade(root) {
    this.root = root;
    this.assetBase = root.getAttribute('data-asset-base') || '';
    this.storeOrigin = (root.getAttribute('data-store-origin') || '').replace(/\/$/, '');
    this.seedBase = root.getAttribute('data-seed') || '';
    this.cols = parseInt(root.getAttribute('data-cols'), 10) || 6;
    this.rows = parseInt(root.getAttribute('data-rows'), 10) || 7;
    this.reducedMotion = REDUCED;

    this.fidgetById = {};
    CAT.fidgets.forEach(function (f) { this.fidgetById[f.id] = f; }, this);

    this.game = null;
    this.boardFidgets = [];     // this board's gems (fidget objects, one per interest cluster)
    this.timer = null;
    this.timeLeft = ROUND_SECONDS;
    this.selectedGem = null;
    this.busy = false;
    this.ended = false;
    this.mode = 'random';       // random | daily | shared
    this.goal = null;           // this round's mini-goal
    this.queuedMove = null;     // one buffered move while a cascade resolves

    // Shareable challenge link: ?game=fidget&seed=X(&grid=CxR). Consumed by
    // the first round started from the intro, then cleared so Play again
    // rolls a fresh board. grid pins the exact board the sharer played.
    this.sharedSeed = null;
    this.sharedGrid = null;
    try {
      var qs = new URLSearchParams(window.location.search);
      if (qs.get('game') === 'fidget' && qs.get('seed')) {
        this.sharedSeed = qs.get('seed');
        var gm = /^([5-8])x([5-9])$/.exec(qs.get('grid') || '');
        if (gm) this.sharedGrid = { cols: parseInt(gm[1], 10), rows: parseInt(gm[2], 10) };
      }
    } catch (e) {}
  }

  FidgetArcade.prototype.tileUrl = function (f) {
    return f && f.tile ? (this.assetBase + f.tile) : '';
  };
  FidgetArcade.prototype.productUrl = function (f) {
    var q = f.variantId ? ('?variant=' + f.variantId) : '';
    return (this.storeOrigin || '') + '/products/' + f.handle + q;
  };

  /* ---------- screens ---------- */

  FidgetArcade.prototype.boot = function () {
    this.root.classList.add('dfa');
    this.renderIntro();
    track('intro_started', {});
  };

  FidgetArcade.prototype.renderIntro = function () {
    this.ended = false;
    this.setPlayChrome(false);
    this.root.innerHTML = '';
    var screen = el('div', 'dfa-screen dfa-intro');

    // Daily board button meta: today's best + the gentle streak, if any.
    var today = localDayStr();
    var yd = new Date(); yd.setDate(yd.getDate() - 1);
    var yesterday = localDayStr(yd);
    var daily = readJson(DAILY_KEY) || {};
    var dailyBits = [];
    if (daily.day === today && daily.best > 0) dailyBits.push('Your best today: ' + daily.best + '.');
    if (daily.sLast === today && daily.sCount >= 2) dailyBits.push('Day ' + daily.sCount + ' streak.');
    else if (daily.sLast === yesterday && daily.sCount >= 2) dailyBits.push('Play to keep your ' + daily.sCount + ' day streak.');
    if (!dailyBits.length) dailyBits.push('Everyone plays the same board today.');

    var challenge = this.sharedSeed != null;
    screen.innerHTML =
      '<div class="dfa-cab">' +
        '<p class="dfa-eyebrow">Duo Gift Arcade</p>' +
        '<h2 class="dfa-title">Match to find<br>a fidget</h2>' +
        '<p class="dfa-intro__how">Swap two touching fidgets, diagonals count, to line up 3 and snap them off the board. What you keep reaching for tells us the vibe, and we pick a gift to match. No quiz, you play it out.</p>' +
        (challenge ? '<p class="dfa-chip dfa-chip--challenge">Challenge board loaded. Beat their score!</p>' : '') +
        '<button type="button" class="dfa-btn dfa-btn--go" data-dfa-start>' + (challenge ? 'Play the challenge board' : 'Start the round') + '</button>' +
        '<button type="button" class="dfa-btn dfa-btn--ghost dfa-btn--daily" data-dfa-daily>' +
          '<span class="dfa-btn--daily__title">Play today&#39;s board</span>' +
          '<span class="dfa-btn--daily__meta">' + dailyBits.join(' ') + '</span>' +
        '</button>' +
        '<p class="dfa-intro__meta">' + ROUND_SECONDS + ' seconds. Keyboard friendly. Reduced motion supported.</p>' +
        this.soundBtnHtml('dfa-sound--intro') +
      '</div>';
    this.root.appendChild(screen);
    var self = this;
    this.wireSound(screen);
    screen.querySelector('[data-dfa-start]').addEventListener('click', function () {
      SoundFx.unlock(); SoundFx.ui();   // the Start tap is our autoplay-unlock gesture
      if (self.sharedSeed != null) self.startGame(self.sharedSeed, { mode: 'shared', grid: self.sharedGrid });
      else self.startGame();
    });
    screen.querySelector('[data-dfa-daily]').addEventListener('click', function () {
      SoundFx.unlock(); SoundFx.ui();
      self.startGame(null, { mode: 'daily' });
    });
  };

  FidgetArcade.prototype.nowSeed = function () {
    return (window.performance && performance.now ? performance.now() : 0) + (this.seedTick = (this.seedTick || 0) + 1) * 1000;
  };

  // Orientation-adaptive board (deferred P0-1 step 5): a landscape play area
  // (w/h > 1.15) starts the round as a wide 8x6 instead of the portrait 6x7.
  // Measured fresh at every round start, so rotating between rounds adapts.
  // Only applies when the section sits at its 6x7 default; an explicit
  // cols/rows setting in the schema always wins.
  FidgetArcade.prototype.gridForViewport = function () {
    var cols = this.cols, rows = this.rows;
    if (cols === 6 && rows === 7) {
      try {
        var stage = this.root.closest('.fidget-arcade') || this.root;
        var rect = stage.getBoundingClientRect();
        var vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight || 0;
        var h = vh - Math.max(0, rect.top);   // the room the play stage actually gets
        if (h > 0 && rect.width / h > 1.15) { cols = 8; rows = 6; }
      } catch (e) {}
    }
    return { cols: cols, rows: rows };
  };

  // opts.mode: 'random' (default) | 'daily' | 'shared'. Daily seeds from the
  // LOCAL calendar day; daily + shared rounds pin the grid (no viewport
  // adaptation) so one seed means one identical board for everyone.
  FidgetArcade.prototype.startGame = function (seedOverride, opts) {
    opts = opts || {};
    var mode = opts.mode || 'random';
    var seed;
    if (mode === 'daily') seed = 'fidget-daily-' + localDayStr();
    else if (seedOverride != null) seed = seedOverride;
    else seed = this.seedBase ? this.seedBase : ('fidget-' + Math.floor(this.nowSeed()));
    this.mode = mode;
    this.seedUsed = seed;
    if (mode === 'shared') this.sharedSeed = null;   // consumed; Play again rolls fresh
    // Pick 7 gems, each a different interest cluster, deterministic from seed
    // (every round draws a fresh 7 of the 28 catalog products, so the game
    // shows more of the store across replays). 7 types stays matchable because
    // swaps are 8-way: diagonals count (restored 2026-07-05, owner call), which
    // roughly doubles the legal moves a board offers versus orthogonal-only.
    this.boardFidgets = REC.pickBoard({ catalog: CAT, seed: seed, n: 7 });
    var pool = this.boardFidgets.map(function (f) { return f.id; });
    var grid;
    if (opts.grid) grid = opts.grid;
    else if (mode === 'random') grid = this.gridForViewport();
    else grid = { cols: this.cols, rows: this.rows };
    this.game = E.createGame({ seed: seed, gemPool: pool, board: { cols: grid.cols, rows: grid.rows } });
    // One rotating mini-goal per round, deterministic from the seed so a
    // shared or daily board carries the same goal for everyone. The bonus is
    // TIME, never points: the engine owns the score, and a UI-side score
    // bonus would break future server-side replay.
    var gh = E.hashSeed(String(seed) + ':goal');
    this.goal = {
      f: this.boardFidgets[gh % this.boardFidgets.length],
      target: 10 + ((gh >>> 3) % 4),
      shown: 0,
      done: false
    };
    this.timeLeft = ROUND_SECONDS;
    this.shownScore = 0;
    this.selectedGem = null;
    this.busy = false;
    this.ended = false;
    this.queuedMove = null;
    track('intro_completed', {});
    track('game_started', { seed: String(seed), board: pool.join(','), mode: mode, goal: this.goal.f.id, goalTarget: this.goal.target });
    this.renderGame();
    this.startTimer();
  };

  FidgetArcade.prototype.renderGame = function () {
    var g = this.game;
    this.setPlayChrome(true);
    this.root.innerHTML = '';
    var screen = el('div', 'dfa-screen dfa-play');
    screen.innerHTML =
      '<div class="dfa-hud">' +
        '<div class="dfa-hud__score"><span class="dfa-hud__label">Score</span>' +
          '<strong class="dfa-odo" data-dfa-score>' +
            '<span class="visually-hidden" data-dfa-score-sr aria-live="polite">0</span>' +
            '<span class="dfa-odo__wrap" data-dfa-odo aria-hidden="true"></span>' +
          '</strong></div>' +
        '<div class="dfa-hud__time"><span class="dfa-hud__label">Time</span><strong data-dfa-time>' + ROUND_SECONDS + '</strong></div>' +
        '<div class="dfa-hud__btns">' +
          this.soundBtnHtml('') +
          '<button type="button" class="dfa-btn dfa-btn--ghost" data-dfa-done>See picks</button>' +
        '</div>' +
      '</div>' +
      this.goalChipHtml() +
      '<div class="dfa-boardwrap">' +
        '<div class="dfa-board" data-dfa-board role="grid" aria-label="Fidget match board. Swap two touching fidgets in any direction, diagonals count, to clear a line of three."></div>' +
      '</div>' +
      '<p class="visually-hidden" aria-live="polite" data-dfa-announce></p>';
    this.root.appendChild(screen);

    this.screenEl = screen;   // positioning layer for fly-to-chip + popups
    this.boardEl = screen.querySelector('[data-dfa-board]');
    this.scoreEl = screen.querySelector('[data-dfa-score]');
    this.scoreSrEl = screen.querySelector('[data-dfa-score-sr]');
    this.odoEl = screen.querySelector('[data-dfa-odo]');
    this.timeEl = screen.querySelector('[data-dfa-time]');
    this.announceEl = screen.querySelector('[data-dfa-announce]');
    this.goalEl = screen.querySelector('[data-dfa-goal]');
    this.goalCountEl = screen.querySelector('[data-dfa-goal-count]');
    this.shownScore = 0;
    this.setScore(0);
    if (this.goal) this.announce('Round goal: clear ' + this.goal.target + ' ' + this.goal.f.short + ' cards.');

    this.boardEl.style.setProperty('--dfa-cols', g.cols);
    this.boardEl.style.setProperty('--dfa-rows', g.rows);

    this.buildTiles();

    var self = this;
    this.wireSound(screen);
    screen.querySelector('[data-dfa-done]').addEventListener('click', function () { self.endGame('manual'); });
    this.bindBoard();
    this.lastInputAt = Date.now();
    this._hintTiles = null;
    this._coach = null;
    this.maybeCoachMark();

    // Size the board to the room left below the HUD so the play screen fits
    // without clipping. The first measure can land before fonts
    // and the icon images settle (board top measured too low, so the board is
    // sized too small until a later zoom/resize re-fits it), so re-fit across the
    // next frames, on fonts ready, on window load, and whenever the board's
    // container actually resizes. fitBoard is idempotent, so this cannot loop.
    var refit = function () { self.fitBoard(); };
    requestAnimationFrame(function () { refit(); requestAnimationFrame(refit); });
    setTimeout(refit, 320);   // the dfa-in entry animation translates the screen ~10px; re-measure after it settles
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(refit).catch(function () {}); }
    if (!this._fitBound) {
      this._fitBound = true;
      var raf = 0;
      var schedule = function () { if (raf) return; raf = requestAnimationFrame(function () { raf = 0; self.fitBoard(); }); };
      window.addEventListener('resize', schedule);
      window.addEventListener('load', schedule);
      if (window.visualViewport) window.visualViewport.addEventListener('resize', schedule);
      if (window.ResizeObserver) {
        var wrap = self.boardEl && self.boardEl.parentNode;
        if (wrap) { try { new ResizeObserver(schedule).observe(wrap); } catch (e) {} }
      }
    }
  };

  // While playing, the stage + cabinet drop their roomy intro paddings so the
  // board gets the vertical budget (intro/reveal keep the roomy look). The
  // standalone section sets stage padding via an inline style attribute, so the
  // play-time override is inline too (a stylesheet rule could never win).
  FidgetArcade.prototype.setPlayChrome = function (on) {
    this.root.classList.toggle('is-playing', !!on);
    var stage = this.root.closest('.fidget-arcade');
    if (!stage) return;
    stage.classList.toggle('is-playing', !!on);
    if (on) {
      if (this._stagePad == null) this._stagePad = [stage.style.paddingTop || '', stage.style.paddingBottom || ''];
      stage.style.paddingTop = '12px';
      stage.style.paddingBottom = '12px';
      // the stage's document offset (theme header etc.), so the play stage can
      // fill exactly the viewport below it (zero page scroll during a round)
      var top = Math.max(0, Math.round(stage.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0)));
      stage.style.setProperty('--dfa-stage-top', top + 'px');
    } else if (this._stagePad) {
      stage.style.paddingTop = this._stagePad[0];
      stage.style.paddingBottom = this._stagePad[1];
      stage.style.removeProperty('--dfa-stage-top');
      this._stagePad = null;
    }
  };

  // Board tiles stay crisp up to ~92px (SVG art + drop shadows), and the play
  // cabinet may grow past the 560px intro width up to this ceiling on tall
  // desktop viewports.
  var TILE_CAP = 92;
  var CAB_CAP = 720;

  // Largest board that fits the vertical room left below its top edge,
  // preserving the cols/rows aspect ratio, then shrink-wrap the cabinet to it
  // (--dfa-cab-w) so a height-bound board never floats in dead margins.
  // Every input is independent of the board's CURRENT size (fixed paddings are
  // measured as differences, the height budget from the viewport), so the
  // ResizeObserver that watches the wrapper settles after one write.
  FidgetArcade.prototype.fitBoard = function () {
    var board = this.boardEl;
    if (!board || !this.game || !board.isConnected) return;
    var g = this.game;
    var root = this.root;
    var wrap = board.parentNode;                 // .dfa-boardwrap
    var stage = root.closest('.fidget-arcade');
    var rootRect = root.getBoundingClientRect();
    var boardRect = board.getBoundingClientRect();

    // Fixed horizontal chrome: cabinet edge -> board slot (paddings + border).
    var wcs = window.getComputedStyle(wrap);
    var wrapPadX = (parseFloat(wcs.paddingLeft) || 0) + (parseFloat(wcs.paddingRight) || 0);
    var chromeX = Math.max(0, rootRect.width - (wrap.clientWidth - wrapPadX));

    // Fixed vertical chrome below the board: wrap/screen/cabinet bottom
    // paddings, plus the stage's bottom padding outside the cabinet.
    var stagePadB = 0;
    if (stage) stagePadB = parseFloat(window.getComputedStyle(stage).paddingBottom) || 0;
    var belowChrome = Math.max(0, rootRect.bottom - boardRect.bottom) + stagePadB;

    // Height budget: visualViewport is the truth on mobile (browser toolbars).
    var vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
    var avail = vh - boardRect.top - belowChrome;
    if (avail < 132) avail = 132;                // never collapse on a tiny window
    // Height -> width through the tile grid, not the naive aspect ratio: the
    // rendered board is rows*tile + (rows-1)*gap tall, which runs
    // gap*(rows-cols)/cols past the aspect box and would leak ~1px of page
    // scroll on viewports where the height budget lands exactly.
    var gap = parseFloat(window.getComputedStyle(board).getPropertyValue('--dfa-gap')) || 7;
    var tileByH = (avail - (g.rows - 1) * gap) / g.rows;
    var byHeight = tileByH * g.cols + (g.cols - 1) * gap;

    // Width budget: what the stage offers the cabinet (capped), minus chrome.
    var stageW = Infinity;
    if (stage) {
      var scs = window.getComputedStyle(stage);
      stageW = stage.clientWidth - (parseFloat(scs.paddingLeft) || 0) - (parseFloat(scs.paddingRight) || 0);
    }
    var byWidth = Math.min(CAB_CAP, stageW) - chromeX;

    // Tile-size ceiling so the art never blurs on huge viewports.
    var byTile = g.cols * TILE_CAP + (g.cols - 1) * gap;

    var px = Math.floor(Math.max(132, Math.min(byHeight, byWidth, byTile)));
    var next = px + 'px';
    if (board.style.width !== next) board.style.width = next; // idempotent: no ResizeObserver loop
    var cabW = Math.ceil(px + chromeX) + 'px';
    if (root.style.getPropertyValue('--dfa-cab-w') !== cabW) root.style.setProperty('--dfa-cab-w', cabW);
  };

  /* ---------- sound toggle + particle burst (pure UI polish) ---------- */

  FidgetArcade.prototype.soundIcon = function (muted) {
    return muted
      ? '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16 9.5l5 5M21 9.5l-5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>'
      : '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16.5 8.6a5 5 0 010 6.8M19 6.2a8 8 0 010 11.6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>';
  };
  FidgetArcade.prototype.soundBtnHtml = function (extra) {
    var m = SoundFx.isMuted();
    return '<button type="button" class="dfa-iconbtn ' + (extra || '') + '" data-dfa-sound ' +
      'aria-pressed="' + (m ? 'true' : 'false') + '" ' +
      'aria-label="' + (m ? 'Unmute sounds' : 'Mute sounds') + '" ' +
      'title="' + (m ? 'Sound off' : 'Sound on') + '">' + this.soundIcon(m) + '</button>';
  };
  FidgetArcade.prototype.wireSound = function (container) {
    var self = this;
    var btn = container.querySelector('[data-dfa-sound]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var m = !SoundFx.isMuted();
      SoundFx.setMuted(m);
      if (!m) { SoundFx.unlock(); SoundFx.ui(); }   // unmuting is also a gesture
      btn.setAttribute('aria-pressed', m ? 'true' : 'false');
      btn.setAttribute('aria-label', m ? 'Unmute sounds' : 'Mute sounds');
      btn.setAttribute('title', m ? 'Sound off' : 'Sound on');
      btn.innerHTML = self.soundIcon(m);
    });
  };

  // A confetti pop anchored to a tile, fired when a big merge (match-4 / 5)
  // mints a power-up. GPU transform + opacity only; skipped under reduced
  // motion. Cosmetic, so Math.random() for spray direction is fine.
  FidgetArcade.prototype.burst = function (idx, kind) {
    if (this.reducedMotion || !this.boardEl) return;
    var anchor = this.tiles[idx];
    if (!anchor) return;
    var rainbow = kind === 'rainbow';
    var count = rainbow ? 22 : (kind === 'bomb' ? 16 : 12);
    var palette = ['#fe3fa4', '#fc5522', '#f9db1b', '#12b65f', '#188df3', '#786af9'];
    var cx = anchor.offsetLeft + anchor.offsetWidth / 2;
    var cy = anchor.offsetTop + anchor.offsetHeight / 2;
    var spread = anchor.offsetWidth * (rainbow ? 1.7 : 1.15);
    var made = [];
    for (var i = 0; i < count; i += 1) {
      var p = document.createElement('span');
      p.className = 'dfa-particle';
      var ang = (Math.PI * 2) * (i / count) + Math.random() * 0.6;
      var dist = spread * (0.45 + Math.random() * 0.85);
      var col = palette[i % palette.length];
      var sz = (rainbow ? 8 : 6) * (0.6 + Math.random() * 0.9);
      p.style.left = cx + 'px';
      p.style.top = cy + 'px';
      p.style.width = p.style.height = sz.toFixed(1) + 'px';
      p.style.background = col;
      p.style.boxShadow = '0 0 6px ' + col;
      p.style.setProperty('--dx', (Math.cos(ang) * dist).toFixed(1) + 'px');
      p.style.setProperty('--dy', (Math.sin(ang) * dist + spread * 0.35).toFixed(1) + 'px');
      p.style.animationDelay = (Math.random() * 50).toFixed(0) + 'ms';
      this.boardEl.appendChild(p);
      made.push(p);
    }
    setTimeout(function () { made.forEach(function (p) { if (p.parentNode) p.parentNode.removeChild(p); }); }, 820);
  };

  FidgetArcade.prototype.buildTiles = function () {
    var g = this.game;
    this.tiles = new Array(g.cols * g.rows);
    // `visual`/`vspec` mirror what is currently DISPLAYED so we can animate the
    // cascade (the logical board jumps straight to its settled state inside
    // applyMove; we replay the recorded steps over the visual board to show it).
    this.visual = g.cells.slice();
    this.vspec = g.specials.slice();
    var frag = document.createDocumentFragment();
    for (var r = 0; r < g.rows; r += 1) {
      for (var c = 0; c < g.cols; c += 1) {
        var i = r * g.cols + c;
        var btn = el('button', 'dfa-gem');
        btn.type = 'button';
        btn.setAttribute('role', 'gridcell');
        btn.dataset.r = r; btn.dataset.c = c;
        btn.style.gridRow = (r + 1); btn.style.gridColumn = (c + 1);
        btn.tabIndex = (i === 0) ? 0 : -1;
        this.tiles[i] = btn;
        frag.appendChild(btn);
        this.setTileContent(i, g.cells[i], g.specials[i]);
      }
    }
    this.boardEl.appendChild(frag);
  };

  // The inner markup of a tile: the fidget art, plus a power-up overlay badge.
  FidgetArcade.prototype.gemInner = function (id, special) {
    var f = id ? this.fidgetById[id] : null;
    var ico = f ? ICONS.svg(f.id, 'dfa-ico') : '';
    var fx = special ? '<span class="dfa-gem__fx dfa-gem__fx--' + special + '" aria-hidden="true"></span>' : '';
    return '<span class="dfa-gem__inner">' + ico + fx + '</span>';
  };

  // One prebuilt inner node per (fidget, power-up) pair, cloned on demand.
  // Parsing the SVG art through innerHTML on every cascade frame was the
  // profiled hotspot (syncAllTiles alone rebuilt all 42 tiles after each
  // move, ~12ms of a 50ms frame budget on a throttled phone trace).
  FidgetArcade.prototype.innerFor = function (id, special) {
    var cache = this._innerCache || (this._innerCache = {});
    var key = id + '|' + (special || '');
    var node = cache[key];
    if (!node) {
      node = cache[key] = el('div', null, this.gemInner(id, special)).firstChild;
    }
    return node.cloneNode(true);
  };

  // Set the gem shown in a slot (id=null clears it). Drives both the initial
  // render and every cascade frame. Skips tiles already displaying the same
  // content, so the settle-state sync after a move touches almost nothing.
  FidgetArcade.prototype.setTileContent = function (i, id, special) {
    var btn = this.tiles[i];
    if (!btn) return;
    var key = (id || '') + '|' + (special || '');
    if (btn.__dfaKey === key) return;
    btn.__dfaKey = key;
    var f = id ? this.fidgetById[id] : null;
    if (id) {
      var inner = this.innerFor(id, special);
      if (btn.firstChild) btn.replaceChild(inner, btn.firstChild);
      else btn.appendChild(inner);
    } else if (btn.firstChild) {
      btn.textContent = '';
    }
    btn.classList.toggle('is-empty', !id);
    btn.classList.toggle('is-special', !!special);
    var label = f ? f.short : 'empty';
    if (f && special) label += ', ' + (SPECIAL_LABEL[special] || 'power-up');
    btn.setAttribute('aria-label', label);
  };

  // Slide a batch of tiles in from above their slots (falls + fresh drops),
  // GPU transform only, then a brief landing squash staggered a touch per
  // column so a full-width refill reads as a wave, not a stamp. All start
  // offsets are written first and ONE forced reflow commits the whole batch;
  // the per-tile offsetWidth read this replaces was the profiled layout
  // thrash (write-read-write across ~30 falling tiles = 100ms+ tasks on a
  // throttled phone trace). No-op under reduced motion (content is set).
  FidgetArcade.prototype.slideInMany = function (list) {
    if (this.reducedMotion || !list.length || !this.boardEl) return;
    var self = this;
    var staged = [];
    list.forEach(function (it) {
      var btn = self.tiles[it.idx];
      if (!btn || !it.dy) return;
      btn.classList.remove('is-land');
      btn.style.transition = 'none';
      btn.style.transform = 'translateY(calc((100% + var(--dfa-gap)) * ' + (-it.dy) + '))';
      staged.push({ btn: btn, col: self.game ? (it.idx % self.game.cols) : 0 });
    });
    if (!staged.length) return;
    void this.boardEl.offsetWidth; // one commit for the whole batch
    staged.forEach(function (s) {
      s.btn.style.transition = 'transform ' + FALL_MS + 'ms cubic-bezier(.2,.72,.28,1)';
      s.btn.style.transform = '';
      setTimeout(function () {
        s.btn.classList.add('is-land');
        setTimeout(function () { s.btn.classList.remove('is-land'); }, 180);
      }, FALL_MS + s.col * 6);
    });
  };

  /* ---------- juice: fly-to-score collection, score pops, combo callouts ----
   * All decorative: transform/opacity only via WAAPI, spawned per cascade
   * step, skipped under reduced motion, and fully guarded so a failure can
   * never break the round. Reads (tile/score rects) are batched before any
   * DOM writes. */

  // Chain-depth praise ladder: deeper cascades earn a bigger, hotter callout.
  // Tier 1 is the classic pop, tier 2 grows and warms up, tier 3 (chain 4+)
  // is the full-width banner moment. Duo voice, no em dashes.
  var CALLOUTS = {
    2: { text: 'Nice snap!', tier: 1 },
    3: { text: 'Chain x3!', tier: 2 },
    4: { text: 'Dialed in!', tier: 3 },
    5: { text: 'Certified fresh!', tier: 3 },
    6: { text: 'Pure arcade magic!', tier: 3 }
  };

  FidgetArcade.prototype.flyAndPop = function (step) {
    if (this.reducedMotion || !this.boardEl || !this.screenEl || !step.cleared.length) return;
    try {
      var self = this;
      var screenRect = this.screenEl.getBoundingClientRect();
      // --- batched reads: cleared-tile rects (one flyer per fidget id, cap 4),
      // the clear centroid for the score pop, and the score counter's center
      // (the flyers' shared target; the arc says "these counted").
      var seen = {}, flights = [];
      var cx = 0, cy = 0, n = 0;
      for (var k = 0; k < step.cleared.length; k += 1) {
        var cl = step.cleared[k];
        var t = this.tiles[cl.idx];
        if (!t) continue;
        var r = t.getBoundingClientRect();
        var x = r.left + r.width / 2, y = r.top + r.height / 2;
        cx += x; cy += y; n += 1;
        if (!cl.id || seen[cl.id] || flights.length >= 4) continue;
        seen[cl.id] = true;
        flights.push({ id: cl.id, x: x, y: y, w: r.width });
      }
      if (!n) return;
      cx = cx / n - screenRect.left; cy = cy / n - screenRect.top;
      var target = null;
      if (this.scoreEl) {
        var sr = this.scoreEl.getBoundingClientRect();
        target = { x: sr.left + sr.width / 2, y: sr.top + sr.height / 2 };
      }
      // --- writes only from here on.
      if (target) flights.forEach(function (f) {
        var size = Math.max(18, f.w * 0.5);
        var flyer = el('span', 'dfa-flyer', ICONS.svg(f.id, 'dfa-ico'));
        flyer.setAttribute('aria-hidden', 'true');
        flyer.style.width = flyer.style.height = size.toFixed(0) + 'px';
        flyer.style.left = (f.x - screenRect.left - size / 2).toFixed(1) + 'px';
        flyer.style.top = (f.y - screenRect.top - size / 2).toFixed(1) + 'px';
        self.screenEl.appendChild(flyer);
        var dx = target.x - f.x, dy = target.y - f.y;
        if (!flyer.animate) { flyer.remove(); return; }
        var anim = flyer.animate([
          { transform: 'translate(0,0) scale(1)', opacity: 1 },
          { transform: 'translate(' + (dx * 0.5).toFixed(1) + 'px,' + (dy * 0.5 - 34).toFixed(1) + 'px) scale(.8)', opacity: 1, offset: 0.55 },
          { transform: 'translate(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px) scale(.4)', opacity: 0.9 }
        ], { duration: 460, easing: 'cubic-bezier(.2,.7,.3,1)' });
        anim.onfinish = function () {
          if (flyer.parentNode) flyer.parentNode.removeChild(flyer);
          // re-pulse the score as each icon lands so the arc reads as counted
          if (self.scoreEl) {
            self.scoreEl.classList.remove('is-bump'); void self.scoreEl.offsetWidth; self.scoreEl.classList.add('is-bump');
          }
        };
      });
      if (step.gained > 0) {
        var pop = el('span', 'dfa-scorepop', '+' + step.gained);
        pop.setAttribute('aria-hidden', 'true');
        pop.style.left = cx.toFixed(1) + 'px';
        pop.style.top = cy.toFixed(1) + 'px';
        this.screenEl.appendChild(pop);
        setTimeout(function () { if (pop.parentNode) pop.parentNode.removeChild(pop); }, 700);
      }
      if (step.chain >= 2) {
        var co = CALLOUTS[Math.min(6, step.chain)];
        var callout = el('span', 'dfa-callout dfa-callout--t' + co.tier, co.text);
        callout.setAttribute('aria-hidden', 'true');
        this.boardEl.appendChild(callout);
        setTimeout(function () { if (callout.parentNode) callout.parentNode.removeChild(callout); }, co.tier === 3 ? 980 : 820);
      }
      if (step.chain >= 3) this.frameStreak();
    } catch (e) {}
  };

  /* ---------- input: Candy Crush drag + tap + keyboard ----------
   * Grab a tile and it tracks the pointer 1:1 along its dominant swap
   * direction: the nearest of the 8 compass neighbors, diagonals included;
   * the neighbor counter-translates to preview the swap; crossing the
   * half-cell line commits without waiting for release; an early release
   * springs back unless flicked. Tap-select and keyboard play are unchanged.
   * Buttery rules: ALL board geometry is cached at grab time (the pointerdown
   * rect + gap are the gesture's only layout reads), moves are rAF-coalesced,
   * and every visual update is a transform write. */

  var DRAG_COMMIT = 0.5;    // fraction of a cell that commits mid-drag
  var DRAG_MAYBE = 0.35;    // release past here commits only on a flick
  var FLICK_V = 0.5;        // px/ms along the axis, toward the neighbor
  var TAP_SLOP = 6;         // px of total movement that still counts as a tap
  var TAP_MS = 300;

  FidgetArcade.prototype.bindBoard = function () {
    var self = this;
    var drag = null;   // active gesture; null when idle
    var raf = 0;

    function stopRaf() { if (raf) { cancelAnimationFrame(raf); raf = 0; } }

    function springBack(t) {
      if (!t) return;
      t.classList.remove('is-grabbed', 'is-drag-peer');
      if (self.reducedMotion) { t.style.transition = ''; t.style.transform = ''; t.style.zIndex = ''; return; }
      t.style.transition = 'transform 150ms cubic-bezier(.2,.7,.3,1)';
      t.style.transform = '';
      setTimeout(function () { t.style.transition = ''; t.style.zIndex = ''; }, 160);
    }

    function endDrag() {
      stopRaf();
      if (drag && drag.tile) { try { drag.tile.releasePointerCapture(drag.pid); } catch (e) {} }
      drag = null;
    }

    function cancelDrag() {
      if (!drag) return;
      if (!drag.silent) {
        springBack(drag.tile);
        if (drag.peer) springBack(drag.peer);
      }
      endDrag();
    }

    // Commit the previewed swap NOW (mid-drag or on a flick release). The two
    // tiles keep their inline drag transforms; attemptSwap(a, b, true) starts
    // the swap animation from them, so there is no snap-back hitch. A silent
    // gesture (started mid-cascade) wrote no transforms; its commit lands in
    // attemptSwap's one-move buffer instead.
    function commit() {
      var d = drag;
      if (!d || !d.peerCell) return;
      var a = d.cell, b = d.peerCell;
      if (!d.silent) {
        d.tile.classList.remove('is-grabbed');
        if (d.peer) d.peer.classList.remove('is-drag-peer');
      }
      endDrag();
      self.attemptSwap(a, b, !d.silent);
    }

    // One write pass per frame: dominant direction (nearest of the 8 compass
    // neighbors, diagonals included), elastic clamp along it, tile + neighbor
    // transforms, midpoint commit. Zero layout reads in here.
    // OCTS[k] = [row, col] step for the octant at k * 45deg, screen space
    // (y grows downward, matching rows).
    var OCTS = [[0, 1], [1, 1], [1, 0], [1, -1], [0, -1], [-1, -1], [-1, 0], [-1, 1]];
    function flushMove() {
      raf = 0;
      var d = drag;
      if (!d) return;
      var dx = d.px - d.x0, dy = d.py - d.y0;
      if (!d.moved) {
        if (dx * dx + dy * dy < TAP_SLOP * TAP_SLOP) return;   // still a tap
        d.moved = true;
        if (!d.silent) {
          d.tile.classList.add('is-grabbed');
          d.tile.style.zIndex = 6;
        }
      }
      var oct = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) & 7;
      var dr = OCTS[oct][0], dc = OCTS[oct][1];
      var len = (dr && dc) ? Math.SQRT2 : 1;     // a diagonal cell sits sqrt(2) steps away
      var ux = dc / len, uy = dr / len;          // unit vector toward the neighbor
      var cellDist = d.step * len;               // pointer travel that equals one cell
      var ni = self.cellIndex(d.cell[0] + dr, d.cell[1] + dc);
      var peer = ni >= 0 ? self.tiles[ni] : null;

      // travel along the dominant direction (>= 0: the octant tracks the pointer)
      var proj = dx * ux + dy * uy;
      var disp;
      if (!peer) disp = Math.min(proj, d.step) * 0.25;                       // board edge: pure resistance
      else if (proj > cellDist) disp = cellDist + (proj - cellDist) * 0.25;  // past one cell: elastic
      else disp = proj;                                                      // 1:1

      if (peer !== d.peer) {   // dominant direction changed: re-aim the preview
        if (d.peer && !d.silent) { d.peer.classList.remove('is-drag-peer'); d.peer.style.transform = ''; d.peer.style.zIndex = ''; }
        d.peer = peer;
        d.peerCell = peer ? [d.cell[0] + dr, d.cell[1] + dc] : null;
        if (peer && !d.silent) { peer.classList.add('is-drag-peer'); peer.style.zIndex = 5; }
      }

      d.ux = ux; d.uy = uy; d.cellDist = cellDist;
      // a silent gesture never writes transforms: the cascade animation owns
      // the tiles right now, so the drag is intent-tracking only
      if (!d.silent) {
        d.tile.style.transform = 'translate(' + (ux * disp).toFixed(1) + 'px,' + (uy * disp).toFixed(1) + 'px) scale(1.08)';
        if (peer) {
          var back = Math.max(0, Math.min(cellDist, proj));
          peer.style.transform = 'translate(' + (-ux * back).toFixed(1) + 'px,' + (-uy * back).toFixed(1) + 'px)';
        }
      }

      if (peer && proj >= cellDist * DRAG_COMMIT) commit();   // the snappy classic feel
    }

    this.boardEl.addEventListener('pointerdown', function (e) {
      SoundFx.unlock();   // first board touch is also a valid autoplay-unlock gesture
      self.noteInput();
      if (self.ended || drag) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      var t = e.target.closest('.dfa-gem');
      if (!t || t.classList.contains('is-empty')) return;
      var rect = t.getBoundingClientRect();   // the gesture's only layout read
      var gap = parseFloat(window.getComputedStyle(self.boardEl).getPropertyValue('--dfa-gap')) || 7;
      drag = {
        pid: e.pointerId, tile: t,
        cell: [parseInt(t.dataset.r, 10), parseInt(t.dataset.c, 10)],
        x0: e.clientX, y0: e.clientY,
        px: e.clientX, py: e.clientY, pt: e.timeStamp, t0: e.timeStamp,
        step: rect.width + gap,
        vx: 0, vy: 0, moved: false, peer: null, peerCell: null,
        ux: 1, uy: 0, cellDist: rect.width + gap,
        // mid-cascade grab: track the intent silently (no transforms, the
        // cascade owns the tiles) and buffer the committed move
        silent: self.busy
      };
      try { t.setPointerCapture(e.pointerId); } catch (err) {}
    }, { passive: true });

    this.boardEl.addEventListener('pointermove', function (e) {
      var d = drag;
      if (!d || e.pointerId !== d.pid) return;
      var dt = e.timeStamp - d.pt;
      if (dt > 0) { d.vx = (e.clientX - d.px) / dt; d.vy = (e.clientY - d.py) / dt; }
      d.px = e.clientX; d.py = e.clientY; d.pt = e.timeStamp;
      if (!raf) raf = requestAnimationFrame(flushMove);
    }, { passive: true });

    this.boardEl.addEventListener('pointerup', function (e) {
      var d = drag;
      if (!d || e.pointerId !== d.pid) return;
      d.px = e.clientX; d.py = e.clientY;
      stopRaf();
      flushMove();          // land on the final position; may commit
      d = drag;
      if (!d) return;       // committed during the flush
      if (!d.moved) {
        var cell = d.cell, dur = e.timeStamp - d.t0;
        endDrag();
        if (dur <= TAP_MS) self.onPick(cell);   // tap: existing select/swap flow
        return;
      }
      // moved but under the commit line: flick toward the neighbor or spring back
      var proj = (d.px - d.x0) * d.ux + (d.py - d.y0) * d.uy;
      var toward = d.vx * d.ux + d.vy * d.uy;   // velocity component toward the neighbor
      if (d.peerCell && proj >= d.cellDist * DRAG_MAYBE && toward >= FLICK_V) { commit(); return; }
      cancelDrag();
    });

    this.boardEl.addEventListener('pointercancel', cancelDrag);

    this.boardEl.addEventListener('keydown', function (e) {
      var t = e.target.closest('.dfa-gem'); if (!t) return;
      self.noteInput();
      var r = parseInt(t.dataset.r, 10), c = parseInt(t.dataset.c, 10);
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); self.onPick([r, c]); return; }
      var nr = r, nc = c;
      if (e.key === 'ArrowRight') nc += 1;
      else if (e.key === 'ArrowLeft') nc -= 1;
      else if (e.key === 'ArrowUp') nr -= 1;
      else if (e.key === 'ArrowDown') nr += 1;
      else return;
      e.preventDefault();
      if (self.cellIndex(nr, nc) < 0) return;
      if (self.selectedGem && self.adjacent(self.selectedGem, [nr, nc]) &&
          self.selectedGem[0] === r && self.selectedGem[1] === c) {
        self.attemptSwap([r, c], [nr, nc]);
      }
      self.focusCell(nr, nc);
    });
  };

  // Note: onPick works while a cascade resolves too. Selection is positional
  // (class toggles only), and a swap completed mid-cascade lands in
  // attemptSwap's one-move buffer.
  FidgetArcade.prototype.onPick = function (cell) {
    if (!this.selectedGem) { this.selectedGem = cell; this.markSelected(cell, true); return; }
    if (this.selectedGem[0] === cell[0] && this.selectedGem[1] === cell[1]) {
      this.markSelected(cell, false); this.selectedGem = null; return;
    }
    if (this.adjacent(this.selectedGem, cell)) { this.attemptSwap(this.selectedGem, cell); }
    else { this.markSelected(this.selectedGem, false); this.selectedGem = cell; this.markSelected(cell, true); }
  };

  FidgetArcade.prototype.markSelected = function (cell, on) {
    var i = cell[0] * this.game.cols + cell[1];
    if (this.tiles[i]) this.tiles[i].classList.toggle('is-selected', !!on);
  };
  FidgetArcade.prototype.cellIndex = function (r, c) {
    if (r < 0 || c < 0 || r >= this.game.rows || c >= this.game.cols) return -1;
    return r * this.game.cols + c;
  };
  // 8-neighbour: orthogonal or diagonal swaps, matching drag and the engine
  // default (diagonals restored 2026-07-05, owner call).
  FidgetArcade.prototype.adjacent = function (a, b) {
    var dr = Math.abs(a[0] - b[0]), dc = Math.abs(a[1] - b[1]);
    return dr <= 1 && dc <= 1 && dr + dc > 0;
  };
  FidgetArcade.prototype.focusCell = function (r, c) {
    var i = this.cellIndex(r, c); if (i < 0) return;
    this.tiles.forEach(function (t) { t.tabIndex = -1; });
    this.tiles[i].tabIndex = 0; this.tiles[i].focus();
  };

  /* ---------- move + settle ---------- */

  // fromDrag: the two tiles arrive still carrying their inline drag offsets;
  // every outcome must resolve FROM those offsets (swap animates from them,
  // invalid/activation springs them home) or the gesture ends with a hitch.
  FidgetArcade.prototype.attemptSwap = function (a, b, fromDrag) {
    if (!this.game) return;
    var self = this;
    var ia = a[0] * this.game.cols + a[1];
    var ib = b[0] * this.game.cols + b[1];
    if (this.busy) {
      // Input buffering: hold exactly ONE move while the cascade resolves so a
      // lined-up next swap never feels swallowed (the latest intent wins). It
      // fires the moment the board settles; if the cascade reshaped the board
      // it just resolves as a normal, possibly invalid, swap. Zero UI.
      if (!this.ended) this.queuedMove = [a, b];
      if (fromDrag) this.springTiles(ia, ib, false);
      if (this.selectedGem) { this.markSelected(this.selectedGem, false); this.selectedGem = null; }
      return;
    }
    if (this.selectedGem) { this.markSelected(this.selectedGem, false); this.selectedGem = null; }
    // Snapshot the visual board BEFORE the move; the engine jumps straight to
    // the settled state but hands back rec.steps so we can animate the cascade.
    var visual = this.game.cells.slice();
    var vspec = this.game.specials.slice();
    var rec = { steps: [] };
    var res = E.applyMove(this.game, a, b, rec);
    if (!res.ok) {
      if (fromDrag) this.springTiles(ia, ib, true);
      else { this.nudge(ia); this.nudge(ib); }
      this.announce('No match there.'); SoundFx.invalid(); return;
    }

    this.busy = true;
    this.clearIdleHint();
    this.dismissCoachMark();   // the first successful move graduates the player
    if (fromDrag && (res.activated || this.reducedMotion)) this.springTiles(ia, ib, false);

    // Earned time: +2s per minted power-up, +3s per detonation step, capped at
    // TIME_CAP. UI-timer only; the engine knows nothing about the clock.
    var bonus = 0;
    (res.steps || []).forEach(function (st) {
      if (st.creations && st.creations.length) bonus += 2 * st.creations.length;
      if (st.effect) bonus += 3;
    });
    if (bonus > 0) this.addTime(bonus);
    if (!res.activated) SoundFx.move();   // activation makes its own detonation sound

    function finish() {
      self.animateResolution(visual, vspec, res.steps || [], function () {
        self.announce(
          (res.activated ? 'Power-up fired. ' : '') +
          'Cleared ' + res.cleared + ' fidgets. Plus ' + res.gained + ' points' +
          (res.chain > 1 ? ', ' + res.chain + ' chain.' : '.')
        );
        self.busy = false;
        self.lastInputAt = Date.now();   // idle-hint clock restarts once the board settles
        if (self.timeLeft <= 0 && !self.ended) self.endGame('time');
        self.flushQueuedMove();          // the buffered move fires on settle
      });
    }

    if (res.activated || this.reducedMotion) {
      // A power-up activates in place (the engine does not swap the board), so
      // pulse the gem the player triggered, then detonate.
      if (res.activated && !this.reducedMotion) {
        var sp = this.vspec[ia] ? ia : ib;
        var t = this.tiles[sp];
        if (t) { t.classList.remove('is-activate'); void t.offsetWidth; t.classList.add('is-activate'); }
      }
      finish();
    } else {
      // Normal swap: slide the two tiles, reflect the swap on the visual board,
      // then resolve the cascade over it.
      this.animateSwap(ia, ib, a, b);
      var tv = visual[ia]; visual[ia] = visual[ib]; visual[ib] = tv;
      var ts = vspec[ia]; vspec[ia] = vspec[ib]; vspec[ib] = ts;
      setTimeout(function () {
        self.tiles[ia].style.transition = self.tiles[ib].style.transition = '';
        self.tiles[ia].style.transform = self.tiles[ib].style.transform = '';
        self.setTileContent(ia, visual[ia], vspec[ia]);
        self.setTileContent(ib, visual[ib], vspec[ib]);
        finish();
      }, SWAP_MS);
    }
  };

  // Apply the one buffered move once the board settles. No-op if the round
  // ended (or somehow re-busied) in the meantime.
  FidgetArcade.prototype.flushQueuedMove = function () {
    var q = this.queuedMove;
    this.queuedMove = null;
    if (!q || this.busy || this.ended || !this.game) return;
    this.attemptSwap(q[0], q[1]);
  };

  FidgetArcade.prototype.animateSwap = function (ia, ib, a, b) {
    if (this.reducedMotion) return;
    var ta = this.tiles[ia], tb = this.tiles[ib];
    if (!ta || !tb) return;
    var dx = (b[1] - a[1]), dy = (b[0] - a[0]);
    var step = 'calc((100% + var(--dfa-gap)) * ';
    ta.style.transition = tb.style.transition = 'transform ' + SWAP_MS + 'ms cubic-bezier(.2,.7,.3,1)';
    ta.style.transform = 'translate(' + step + dx + '), ' + step + dy + '))';
    tb.style.transform = 'translate(' + step + (-dx) + '), ' + step + (-dy) + '))';
    ta.style.zIndex = tb.style.zIndex = 5;
  };

  // Replay the engine's recorded cascade over the visual board: each step is a
  // clear burst, then survivors fall and fresh gems drop in, then any minted
  // power-up pops. Sequential with tight delays so a long chain still lands in
  // ~1s. reduced-motion snaps to the settled board with no tweening.
  // opts.finale staggers the first step's clears in reading order (the staged
  // time-up detonation run).
  FidgetArcade.prototype.animateResolution = function (visual, vspec, steps, onDone, opts) {
    var self = this;
    if (this.reducedMotion || !steps.length) {
      this.syncAllTiles();
      if (onDone) onDone();
      return;
    }
    var si = 0;
    function runStep() {
      if (si >= steps.length) { self.syncAllTiles(); self.settleWobble(steps.length); if (onDone) onDone(); return; }
      var step = steps[si++];
      // the board had no legal move and was reshuffled: snap to the new layout,
      // wobble it, tell the player. (always the last step in a move.)
      if (step.type === 'reshuffle') {
        self.syncAllTiles();
        if (self.boardEl && !self.reducedMotion) {
          self.boardEl.classList.remove('is-reshuffle'); void self.boardEl.offsetWidth; self.boardEl.classList.add('is-reshuffle');
        }
        self.announce('No moves were left, so the board reshuffled.');
        SoundFx.shuffle();
        setTimeout(runStep, self.reducedMotion ? 0 : 380);
        return;
      }
      self.playEffect(step);
      self.flyAndPop(step);   // fly-to-chip icons + score pop + combo callout
      self.goalProgressStep(step);   // mini-goal counter rolls with the cascade
      // sound: a power-up detonation has its own voice; otherwise a clear blip
      // whose pitch climbs with the cascade chain.
      if (step.effect) { SoundFx.activate(step.effect); buzz(20); }
      else if (step.cleared.length) { SoundFx.clear(step.chain); buzz(10); }
      // Staged finale: the opening detonation wave pops tile by tile in
      // reading order instead of one simultaneous blink. The stagger rides
      // animation-delay on the same clear keyframe; the collapse waits for
      // the last pop.
      var stagger = 0;
      if (opts && opts.finale && step.chain === 1 && step.cleared.length > 1) {
        var per = Math.min(36, Math.max(14, Math.floor(520 / step.cleared.length)));
        var order = step.cleared.slice().sort(function (x, y) { return x.idx - y.idx; });
        order.forEach(function (cl, k) {
          var t = self.tiles[cl.idx];
          if (t) t.style.animationDelay = (k * per) + 'ms';
        });
        stagger = per * (order.length - 1);
      }
      // restart the clear animation in two batched passes (one shared reflow,
      // not one per tile)
      step.cleared.forEach(function (cl) {
        var t = self.tiles[cl.idx];
        if (t) t.classList.remove('is-clearing');
      });
      if (step.cleared.length) void self.boardEl.offsetWidth;
      step.cleared.forEach(function (cl) {
        var t = self.tiles[cl.idx];
        if (t) t.classList.add('is-clearing');
      });
      setTimeout(function () {
        // empty the cleared slots
        step.cleared.forEach(function (cl) {
          visual[cl.idx] = null; vspec[cl.idx] = null;
          var t = self.tiles[cl.idx];
          if (t) { t.classList.remove('is-clearing'); t.style.animationDelay = ''; }
          self.setTileContent(cl.idx, null, null);
        });
        // survivors fall (processed bottom-first by the engine), then fresh
        // gems drop in from above the top edge; both slide in as ONE batch
        var slides = [];
        step.falls.forEach(function (f) {
          var to = f.toRow * self.game.cols + f.col;
          var from = f.fromRow * self.game.cols + f.col;
          visual[to] = f.id; vspec[to] = f.special || null;
          if (visual[from] === f.id) { visual[from] = null; }
          self.setTileContent(to, f.id, f.special || null);
          slides.push({ idx: to, dy: f.toRow - f.fromRow });
        });
        step.spawns.forEach(function (sp) {
          var di = sp.row * self.game.cols + sp.col;
          visual[di] = sp.id; vspec[di] = null;
          self.setTileContent(di, sp.id, null);
          slides.push({ idx: di, dy: sp.row + 1 });
        });
        self.slideInMany(slides);
        // a host transforms into a power-up: pop it, confetti burst, chime
        step.creations.forEach(function (cr) {
          visual[cr.idx] = cr.id; vspec[cr.idx] = cr.type;
          self.setTileContent(cr.idx, cr.id, cr.type);
          var t = self.tiles[cr.idx];
          if (t) { t.classList.remove('is-mint'); void t.offsetWidth; t.classList.add('is-mint'); }
          var kind = cr.type === 'rainbow' ? 'rainbow' : (cr.type === 'bomb' ? 'bomb' : 'line');
          self.burst(cr.idx, kind);
          SoundFx.mint(cr.type);
        });
        if (step.creations.length) buzz(20);
        self.bumpScore(step.gained, step.chain);
        setTimeout(runStep, FALL_MS + STEP_GAP);
      }, CLEAR_MS + stagger);
    }
    runStep();
  };

  // Snap every tile + the score to the engine's settled state, clearing any
  // in-flight transforms. The safety net at the end of every animation.
  FidgetArcade.prototype.syncAllTiles = function () {
    if (!this.tiles || !this.game) return;
    this.visual = this.game.cells.slice();
    this.vspec = this.game.specials.slice();
    for (var i = 0; i < this.tiles.length; i += 1) {
      var t = this.tiles[i];
      if (t) { t.style.transition = ''; t.style.transform = ''; t.style.zIndex = ''; t.classList.remove('is-clearing'); }
      this.setTileContent(i, this.game.cells[i], this.game.specials[i]);
    }
    this.setScore(this.game.score);
    this.syncGoal();
  };

  // Power-up flourish anchored to the blast: a sweeping beam for a line clear,
  // a board shake for a bomb, a prismatic flash for a rainbow / combo. Purely
  // decorative and fully guarded so it can never break the round.
  FidgetArcade.prototype.playEffect = function (step) {
    if (this.reducedMotion || !step.effect || !this.boardEl) return;
    var board = this.boardEl;
    try {
      if (step.effect === 'rainbow' || step.effect === 'combo' || step.effect === 'finale') {
        board.classList.remove('is-flash'); void board.offsetWidth; board.classList.add('is-flash');
        setTimeout(function () { board.classList.remove('is-flash'); }, 460);
        if (step.effect === 'combo') {   // two power-ups merged: name the moment
          var co = el('span', 'dfa-callout dfa-callout--t2', 'Power combo!');
          co.setAttribute('aria-hidden', 'true');
          board.appendChild(co);
          setTimeout(function () { if (co.parentNode) co.parentNode.removeChild(co); }, 900);
        }
      } else if (step.effect === 'bomb') {
        board.classList.remove('is-shake'); void board.offsetWidth; board.classList.add('is-shake');
        setTimeout(function () { board.classList.remove('is-shake'); }, 380);
      } else if (step.effect === 'line' && step.cleared.length) {
        this.lineBeam(step);
      }
    } catch (e) {}
  };

  FidgetArcade.prototype.lineBeam = function (step) {
    var cols = this.game.cols;
    var rowsSeen = {};
    step.cleared.forEach(function (cl) { rowsSeen[Math.floor(cl.idx / cols)] = 1; });
    var anchor = this.tiles[step.cleared[0].idx];
    if (!anchor) return;
    var horiz = Object.keys(rowsSeen).length === 1;
    var beam = el('span', 'dfa-beam ' + (horiz ? 'dfa-beam--h' : 'dfa-beam--v'));
    if (horiz) { beam.style.top = anchor.offsetTop + 'px'; beam.style.height = anchor.offsetHeight + 'px'; }
    else { beam.style.left = anchor.offsetLeft + 'px'; beam.style.width = anchor.offsetWidth + 'px'; }
    this.boardEl.appendChild(beam);
    setTimeout(function () { if (beam.parentNode) beam.parentNode.removeChild(beam); }, 400);
  };

  FidgetArcade.prototype.nudge = function (i) {
    var t = this.tiles[i]; if (!t || this.reducedMotion) return;
    t.classList.remove('is-nudge'); void t.offsetWidth; t.classList.add('is-nudge');
  };

  // A 3+ chain streak heats up the cabinet frame for a beat: an opacity-only
  // inset glow overlay on the play screen, retriggerable per streak.
  FidgetArcade.prototype.frameStreak = function () {
    if (this.reducedMotion || !this.root) return;
    var cab = this.root.querySelector('.dfa-screen');
    if (!cab) return;
    cab.classList.remove('is-streak'); void cab.offsetWidth; cab.classList.add('is-streak');
    clearTimeout(this._streakT);
    this._streakT = setTimeout(function () { cab.classList.remove('is-streak'); }, 950);
  };

  // Micro-life: after a real cascade (2+ steps) the whole board settles with a
  // tiny dip-and-rebound, one composited transform on the grid container.
  FidgetArcade.prototype.settleWobble = function (stepCount) {
    if (this.reducedMotion || !this.boardEl || stepCount < 2) return;
    var b = this.boardEl;
    b.classList.remove('is-settle'); void b.offsetWidth; b.classList.add('is-settle');
    setTimeout(function () { b.classList.remove('is-settle'); }, 320);
  };

  // Micro-life: while the board rests, one random tile does a brief curious
  // wiggle. One tile at a time, several seconds apart, on the tile's INNER
  // node so it can never fight the drag/fall transforms on the button itself.
  FidgetArcade.prototype.maybeShimmer = function () {
    if (this.reducedMotion || !this.tiles || this.busy || this.ended) return;
    if (this._shimmerAt && Date.now() - this._shimmerAt < 5000) return;
    if (Math.random() > 0.34) return;   // cosmetic-only randomness
    var t = this.tiles[Math.floor(Math.random() * this.tiles.length)];
    if (!t || t.classList.contains('is-empty') || !t.firstChild) return;
    this._shimmerAt = Date.now();
    var inner = t.firstChild;
    inner.classList.remove('is-shimmer'); void t.offsetWidth; inner.classList.add('is-shimmer');
    setTimeout(function () { inner.classList.remove('is-shimmer'); }, 950);
  };

  /* ---------- idle hint + first-round coach mark (plan Tier 1 #6) ----------
   * After ~4s without input the two tiles of a legal swap breathe softly
   * (legalMoves is a pure read, no rng). Any input clears it. The very first
   * round ever (localStorage flag) also gets a one-line coach mark, dismissed
   * by the first successful move. */

  FidgetArcade.prototype.noteInput = function () {
    this.lastInputAt = Date.now();
    this.clearIdleHint();
  };

  FidgetArcade.prototype.clearIdleHint = function () {
    if (!this._hintTiles) return;
    this._hintTiles.forEach(function (t) { t.classList.remove('is-hint'); });
    this._hintTiles = null;
  };

  FidgetArcade.prototype.showIdleHint = function () {
    if (this._hintTiles || !this.game || !this.tiles || this.busy || this.ended) return;
    var moves = E.legalMoves(this.game);
    if (!moves.length) return;
    var a = this.tiles[moves[0].a[0] * this.game.cols + moves[0].a[1]];
    var b = this.tiles[moves[0].b[0] * this.game.cols + moves[0].b[1]];
    if (!a || !b) return;
    a.classList.add('is-hint'); b.classList.add('is-hint');
    this._hintTiles = [a, b];
  };

  FidgetArcade.prototype.maybeCoachMark = function () {
    var seen = false;
    try { seen = !!(window.localStorage && localStorage.getItem('duoFidgetPlayed') === '1'); } catch (e) {}
    if (seen) return;
    var self = this;
    setTimeout(function () {
      if (self.ended || !self.root.isConnected || !self.game || self.game.moves > 0 || self._coach || !self.boardEl) return;
      self.showIdleHint();
      var tip = el('span', 'dfa-coach', 'Drag a fidget onto its neighbor to swap.');
      self.boardEl.parentNode.appendChild(tip);   // .dfa-boardwrap (positioned)
      self._coach = tip;
      self.announce('Drag a fidget onto its neighbor to swap.');
    }, 900);
  };

  FidgetArcade.prototype.dismissCoachMark = function () {
    if (this._coach) {
      if (this._coach.parentNode) this._coach.parentNode.removeChild(this._coach);
      this._coach = null;
    }
    try { if (window.localStorage) localStorage.setItem('duoFidgetPlayed', '1'); } catch (e) {}
  };

  // Return two tiles from their dragged offsets to rest; optionally shake them
  // once they land (an invalid drag-swap). Reduced motion resets instantly.
  FidgetArcade.prototype.springTiles = function (ia, ib, nudgeAfter) {
    var self = this;
    [ia, ib].forEach(function (i) {
      var t = self.tiles[i];
      if (!t) return;
      if (self.reducedMotion) { t.style.transition = ''; t.style.transform = ''; t.style.zIndex = ''; return; }
      t.style.transition = 'transform 150ms cubic-bezier(.2,.7,.3,1)';
      t.style.transform = '';
    });
    if (this.reducedMotion) return;
    setTimeout(function () {
      [ia, ib].forEach(function (i) {
        var t = self.tiles[i];
        if (!t) return;
        t.style.transition = ''; t.style.zIndex = '';
        if (nudgeAfter) self.nudge(i);
      });
    }, 160);
  };

  // Rolling odometer: one digit reel per column, transform-only (each reel is
  // a 0-9 stack translated by --odo-d; the CSS owns the row metric). The
  // screen reader copy is a separate visually-hidden live region updated once
  // per landing, never per frame. Reduced motion writes plain text.
  var ODO_REEL = (function () {
    var s = '<span class="dfa-odo__reel">';
    for (var d = 0; d <= 9; d += 1) s += '<span>' + d + '</span>';
    return s + '</span>';
  })();

  FidgetArcade.prototype.setScore = function (value) {
    var v = Math.max(0, Math.round(value || 0));
    this.shownScore = v;
    if (this.scoreSrEl) this.scoreSrEl.textContent = String(v);
    var wrap = this.odoEl;
    if (!wrap) { if (this.scoreEl) this.scoreEl.textContent = String(v); return; }
    if (this.reducedMotion) { wrap.textContent = String(v); return; }
    var str = String(v);
    while (wrap.children.length < str.length) {
      wrap.insertBefore(el('span', 'dfa-odo__digit', ODO_REEL), wrap.firstChild);
    }
    var pad = wrap.children.length - str.length;   // only 0 mid-round; belt + braces
    for (var k = 0; k < wrap.children.length; k += 1) {
      var digit = k < pad ? 0 : (str.charCodeAt(k - pad) - 48);
      wrap.children[k].firstChild.style.setProperty('--odo-d', digit);
    }
  };

  // During a cascade the engine score is already settled, so the HUD rolls a
  // step-by-step shown score instead (each step adds its own gained points);
  // syncAllTiles snaps it to the engine truth after the animation. A 3+ chain
  // landing gets the bigger bump.
  FidgetArcade.prototype.bumpScore = function (gained, chain) {
    this.setScore((this.shownScore || 0) + (gained || 0));
    if (gained > 0 && this.scoreEl && !this.reducedMotion) {
      var cls = (chain || 1) >= 3 ? 'is-bump--big' : 'is-bump';
      this.scoreEl.classList.remove('is-bump', 'is-bump--big'); void this.scoreEl.offsetWidth;
      this.scoreEl.classList.add(cls);
    }
  };

  FidgetArcade.prototype.announce = function (msg) { if (this.announceEl) this.announceEl.textContent = msg; };

  /* ---------- mini-goal: one rotating objective chip per round ----------
   * Seed-deterministic (same board = same goal for everyone). Progress rolls
   * with the cascade animation like the score odometer and snaps to the
   * engine tally (the truth) at every settle. Completion pays TIME, a
   * celebration, and a sharpened gift recommendation; never engine points. */

  FidgetArcade.prototype.goalChipHtml = function () {
    if (!this.goal) return '';
    var f = this.goal.f;
    var modeChip = '';
    if (this.mode === 'daily') modeChip = '<span class="dfa-chip dfa-chip--mode">Today&#39;s board</span>';
    else if (this.mode === 'shared') modeChip = '<span class="dfa-chip dfa-chip--mode">Challenge board</span>';
    var label = 'Round goal: clear ' + this.goal.target + ' ' + f.short + ' cards.';
    return '<div class="dfa-goalrow">' +
      modeChip +
      '<div class="dfa-goal" data-dfa-goal aria-label="' + label + '">' +
        '<span class="dfa-goal__label" aria-hidden="true">Goal</span>' +
        '<span class="dfa-goal__ico" aria-hidden="true">' + ICONS.svg(f.id, 'dfa-ico') + '</span>' +
        '<span class="dfa-goal__count" aria-hidden="true"><b data-dfa-goal-count>0</b>/' + this.goal.target + '</span>' +
        '<svg class="dfa-goal__check" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 8.5l3.5 3.5 7-7.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</div>' +
    '</div>';
  };

  FidgetArcade.prototype.setGoalShown = function (v) {
    if (!this.goal) return;
    var g = this.goal;
    v = Math.min(g.target, v);
    if (v > g.shown) {
      g.shown = v;
      if (this.goalCountEl) this.goalCountEl.textContent = String(v);
      if (this.goalEl && !this.reducedMotion) {
        this.goalEl.classList.remove('is-tick'); void this.goalEl.offsetWidth; this.goalEl.classList.add('is-tick');
      }
    }
    if (!g.done && g.shown >= g.target) this.goalComplete();
  };

  // Per-cascade-step increment (the animated path).
  FidgetArcade.prototype.goalProgressStep = function (step) {
    if (!this.goal || this.goal.done || !step.cleared) return;
    var id = this.goal.f.id, n = 0;
    for (var i = 0; i < step.cleared.length; i += 1) if (step.cleared[i].id === id) n += 1;
    if (n) this.setGoalShown(this.goal.shown + n);
  };

  // Snap to engine truth (covers reduced motion + every settle).
  FidgetArcade.prototype.syncGoal = function () {
    if (!this.goal || !this.game) return;
    this.setGoalShown(this.game.tally[this.goal.f.id] || 0);
  };

  FidgetArcade.prototype.goalComplete = function () {
    var g = this.goal;
    g.done = true;
    if (this.goalEl) this.goalEl.classList.add('is-done');
    this.addTime(5);   // the bonus: earned seconds, same currency as power-ups
    this.announce('Goal complete! You cleared ' + g.target + ' ' + g.f.short + ' cards. Bonus 5 seconds.');
    SoundFx.mint('rainbow');
    buzz(20);
    if (!this.reducedMotion && this.boardEl) {
      var callout = el('span', 'dfa-callout dfa-callout--t2', 'Goal complete!');
      callout.setAttribute('aria-hidden', 'true');
      this.boardEl.appendChild(callout);
      setTimeout(function () { if (callout.parentNode) callout.parentNode.removeChild(callout); }, 900);
    }
    track('goal_completed', { goal: g.f.id, target: g.target });
  };

  /* ---------- timer ---------- */

  FidgetArcade.prototype.startTimer = function () {
    var self = this;
    this.stopTimer();
    this.timer = setInterval(function () {
      // chooser "Back to games" wipes the DOM without a teardown call
      if (self.root && !self.root.isConnected) { self.stopTimer(); return; }
      self.timeLeft -= 1;
      if (self.timeEl) self.timeEl.textContent = String(Math.max(0, self.timeLeft));
      self.setLowTime(self.timeLeft <= 10 && self.timeLeft > 0);
      if (self.timeLeft <= 10 && self.timeLeft > 0) SoundFx.tick();   // last-10s tension
      // idle nudge: after ~4s without input, breathe a legal swap (see plan #6)
      if (!self.busy && !self.ended && Date.now() - (self.lastInputAt || 0) > 4000) self.showIdleHint();
      // tile micro-life: an occasional one-tile shimmer while the board rests
      self.maybeShimmer();
      // mid-cascade time-out defers to attemptSwap's finish(), so the board
      // resolves (and the flourish can fire) instead of cutting to the reveal
      if (self.timeLeft <= 0 && !self.busy) self.endGame('time');
    }, 1000);
  };
  FidgetArcade.prototype.stopTimer = function () { if (this.timer) { clearInterval(this.timer); this.timer = null; } };

  // Red timer + a subtle board edge glow for the last 10 seconds. Reversible:
  // a time bonus can lift the round back out of the danger zone.
  FidgetArcade.prototype.setLowTime = function (low) {
    if (this.timeEl) this.timeEl.classList.toggle('is-low', !!low);
    if (this.boardEl) this.boardEl.classList.toggle('is-endgame', !!low);
  };

  // Earned time bonus: bump the clock (never past TIME_CAP) and float a small
  // "+Ns" at the timer so the reward is legible.
  FidgetArcade.prototype.addTime = function (secs) {
    if (this.ended || secs <= 0) return;
    var before = this.timeLeft;
    this.timeLeft = Math.min(TIME_CAP, this.timeLeft + secs);
    var gained = this.timeLeft - before;
    if (this.timeEl) this.timeEl.textContent = String(Math.max(0, this.timeLeft));
    this.setLowTime(this.timeLeft <= 10 && this.timeLeft > 0);
    if (gained > 0 && this.timeEl && !this.reducedMotion) {
      var pop = el('span', 'dfa-timepop', '+' + gained + 's');
      pop.setAttribute('aria-hidden', 'true');
      this.timeEl.parentNode.appendChild(pop);
      setTimeout(function () { if (pop.parentNode) pop.parentNode.removeChild(pop); }, 900);
    }
  };

  /* ---------- end + recommend ---------- */

  FidgetArcade.prototype.endGame = function (reason) {
    if (this.ended) return;
    this.ended = true;
    this.stopTimer();
    this.clearIdleHint();
    this.queuedMove = null;   // a buffered move never outlives the round
    var self = this;
    // Time-up flourish: every power-up left on the board auto-detonates and
    // feeds the tally before the reveal (the Sugar Crush moment). Only on a
    // natural time-out; "See picks" means the player chose to leave now.
    if (reason === 'time' && !this.busy && this.game && E.detonateAll) {
      var visual = this.game.cells.slice();
      var vspec = this.game.specials.slice();
      var frec = { steps: [] };
      var fin = E.detonateAll(this.game, frec);
      if (fin.ok) {
        this.setLowTime(false);
        this.announce('Time! Your power-ups fired for ' + fin.gained + ' bonus points.');
        SoundFx.finale();   // the big time-up flourish under the detonation run
        this.busy = true;
        // Staged Sugar Crush: a "Finale!" freeze beat over the still board,
        // then the detonations pop in reading order (opts.finale), the score
        // rolls up per step, and the total stamps before the reveal. Reduced
        // motion skips every beat and snaps straight through.
        var run = function () {
          self.animateResolution(visual, vspec, fin.steps || [], function () {
            self.busy = false;
            self.finaleStamp(function () { self.finishReveal(reason); });
          }, { finale: true });
        };
        if (this.reducedMotion || !this.boardEl) { run(); return; }
        try {
          var callout = el('span', 'dfa-callout dfa-callout--t3', 'Finale!');
          callout.setAttribute('aria-hidden', 'true');
          this.boardEl.appendChild(callout);
          setTimeout(function () { if (callout.parentNode) callout.parentNode.removeChild(callout); }, 980);
        } catch (e) {}
        setTimeout(run, 700);
        return;
      }
    }
    this.finishReveal(reason);
  };

  // The finale's closing beat: the final total stamps over the settled board,
  // holds a moment, then hands off to the reveal. Decorative and fully
  // guarded; reduced motion (or a torn-down board) skips straight through.
  FidgetArcade.prototype.finaleStamp = function (next) {
    if (this.reducedMotion || !this.boardEl || !this.boardEl.isConnected) { next(); return; }
    try {
      var stamp = el('div', 'dfa-stamp',
        '<span class="dfa-stamp__label">Final score</span>' +
        '<span class="dfa-stamp__value">' + this.game.score + '</span>');
      stamp.setAttribute('aria-hidden', 'true');
      this.boardEl.appendChild(stamp);
      buzz(20);
      setTimeout(function () {
        if (stamp.parentNode) stamp.parentNode.removeChild(stamp);
        next();
      }, 1150);
    } catch (e) { next(); }
  };

  FidgetArcade.prototype.finishReveal = function (reason) {
    // A completed goal sharpens the recommendation: those clears were
    // intentional, so the goal fidget gets extra weight in the tally COPY
    // handed to the recommender (game.tally itself stays engine truth).
    var tally = this.game.tally;
    if (this.goal && this.goal.done) {
      tally = Object.assign({}, tally);
      tally[this.goal.f.id] = Math.ceil((tally[this.goal.f.id] || 0) * 1.3) + 3;
    }
    this.rec = REC.recommendGifts({ catalog: CAT, tally: tally, maxPicks: 3 });
    track('game_completed', {
      score: this.game.score, moves: this.game.moves, cleared: this.game.clearedTotal,
      reason: reason || 'time', topCluster: this.rec.topCluster,
      picks: this.rec.picks.map(function (p) { return p.id; }).join(','),
      mode: this.mode,
      goal: this.goal ? this.goal.f.id : '',
      goalDone: !!(this.goal && this.goal.done)
    });
    this.showReveal();
  };

  /* ---------- reveal: the gift picks ---------- */

  // Personal best, guest-first localStorage (never keyed on customer id).
  FidgetArcade.prototype.readBest = function () {
    try { var v = parseInt(localStorage.getItem(BEST_KEY), 10); return isNaN(v) ? 0 : v; } catch (e) { return 0; }
  };
  FidgetArcade.prototype.writeBest = function (score) {
    try { if (window.localStorage) localStorage.setItem(BEST_KEY, String(score)); } catch (e) {}
  };

  // Roll the reveal score from 0 to its final value (ease-out). Reduced motion
  // (or a zero score) lands instantly; the final number is always written.
  FidgetArcade.prototype.countUp = function (node, target) {
    if (!node) return;
    if (this.reducedMotion || !window.requestAnimationFrame || target <= 0) { node.textContent = String(target); return; }
    var dur = 900, start = null;
    function tick(ts) {
      if (start == null) start = ts;
      var p = Math.min(1, (ts - start) / dur);
      var eased = 1 - Math.pow(1 - p, 3);
      node.textContent = String(Math.round(target * eased));
      if (p < 1) requestAnimationFrame(tick); else node.textContent = String(target);
    }
    node.textContent = '0';
    requestAnimationFrame(tick);
  };

  // One celebration confetti pop over the reveal header, reusing the board's
  // .dfa-particle spray (same class, keyframe, palette). Cosmetic only;
  // skipped under reduced motion and fully guarded.
  FidgetArcade.prototype.revealBurst = function (screen) {
    if (this.reducedMotion || !screen) return;
    try {
      var head = screen.querySelector('.dfa-reveal__head') || screen;
      var cx = head.offsetLeft + head.offsetWidth / 2;
      var cy = head.offsetTop + head.offsetHeight * 0.55;
      var spread = Math.max(120, Math.min(240, head.offsetWidth * 0.55));
      var palette = ['#fe3fa4', '#fc5522', '#f9db1b', '#12b65f', '#188df3', '#786af9'];
      var made = [];
      for (var i = 0; i < 26; i += 1) {
        var p = document.createElement('span');
        p.className = 'dfa-particle';
        var ang = (Math.PI * 2) * (i / 26) + Math.random() * 0.6;
        var dist = spread * (0.45 + Math.random() * 0.9);
        var col = palette[i % palette.length];
        var sz = 7 * (0.6 + Math.random() * 0.9);
        p.style.left = cx + 'px';
        p.style.top = cy + 'px';
        p.style.width = p.style.height = sz.toFixed(1) + 'px';
        p.style.background = col;
        p.style.boxShadow = '0 0 6px ' + col;
        p.style.setProperty('--dx', (Math.cos(ang) * dist).toFixed(1) + 'px');
        p.style.setProperty('--dy', (Math.sin(ang) * dist + spread * 0.3).toFixed(1) + 'px');
        p.style.animationDelay = (Math.random() * 90).toFixed(0) + 'ms';
        screen.appendChild(p);
        made.push(p);
      }
      setTimeout(function () { made.forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n); }); }, 920);
    } catch (e) {}
  };

  // One star glyph; fill rides currentColor so the on/off state is pure CSS.
  var STAR_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" fill="currentColor"/></svg>';

  FidgetArcade.prototype.showReveal = function () {
    var self = this;
    var rec = this.rec;
    this.setPlayChrome(false);
    this.root.innerHTML = '';
    var screen = el('div', 'dfa-screen dfa-reveal');

    var score = this.game.score;
    var best = this.readBest();
    var isNewBest = score > 0 && score > best;
    if (isNewBest) this.writeBest(score);

    // 1-3 stars at fixed bands; the hint names the next band honestly.
    var stars = 0;
    for (var sb = 0; sb < STAR_BANDS.length; sb += 1) if (score >= STAR_BANDS[sb]) stars = sb + 1;
    var starHint = stars >= 3
      ? 'Three stars. A perfect run!'
      : (STAR_BANDS[stars] - score) + ' more for ' + (stars === 0 ? 'your first star.' : 'the next star.');
    var starsHtml = '<div class="dfa-stars" role="img" aria-label="' + stars + ' of 3 stars.">';
    for (var st = 0; st < 3; st += 1) {
      starsHtml += '<span class="dfa-star' + (st < stars ? ' is-on' : '') + '" style="--dfa-i:' + st + '">' + STAR_SVG + '</span>';
    }
    starsHtml += '</div><p class="dfa-stars__hint">' + starHint + '</p>';

    // Meta lines under the stars: personal best, daily best + streak, played-
    // days streak, goal. Two at most, so the head never turns into a list.
    var lines = [];
    if (isNewBest) lines.push({ cls: ' is-new', text: 'New personal best!' });
    if (this.mode === 'daily') {
      var d = this.recordDaily(score);
      if (d.newBest && !isNewBest) lines.push({ cls: ' is-new', text: 'New daily best!' });
      else if (!d.newBest && d.best > 0) lines.push({ cls: '', text: 'Your best today: ' + d.best });
      if (d.streak >= 2) lines.push({ cls: '', text: 'Daily streak: day ' + d.streak });
    } else if (!isNewBest && best > 0) {
      lines.push({ cls: '', text: 'Your best so far: ' + best });
    }
    var dayStreak = this.bumpDayStreak();
    if (this.mode !== 'daily' && dayStreak >= 2) lines.push({ cls: '', text: 'You have played ' + dayStreak + ' days in a row' });
    if (this.goal && this.goal.done) lines.push({ cls: ' is-new', text: 'Round goal complete!' });
    var bestLine = lines.slice(0, 2).map(function (l) {
      return '<p class="dfa-reveal__best' + l.cls + '">' + l.text + '</p>';
    }).join('');

    var cards = rec.picks.map(function (p, i) {
      // top pick is badged "Top match"; any pick outside the winning cluster
      // (the runner-up filler when a cluster has < maxPicks) reads as a gentle
      // "you may also like" so it never looks like a mismatch.
      var badge = '';
      if (i === 0) badge = '<span class="dfa-pick__badge">Top match</span>';
      else if (p.cluster !== rec.topCluster) badge = '<span class="dfa-pick__badge dfa-pick__badge--alt">You may also like</span>';
      return '<article class="dfa-pick' + (i === 0 ? ' is-top' : '') + '" style="--dfa-i:' + i + '">' +
        badge +
        '<a class="dfa-pick__media" href="' + self.productUrl(p) + '" target="_blank" rel="noopener">' +
          '<img alt="' + p.title + '" loading="lazy" src="' + self.tileUrl(p) + '">' +
        '</a>' +
        '<div class="dfa-pick__body">' +
          '<strong class="dfa-pick__title">' + p.short + '</strong>' +
          '<span class="dfa-pick__price">' + money(p.price) + '</span>' +
        '</div>' +
        '<button type="button" class="dfa-btn dfa-btn--go dfa-pick__add" data-variant="' + p.variantId + '" data-handle="' + p.handle + '" data-id="' + p.id + '">Add to cart</button>' +
        '<a class="dfa-pick__link" href="' + self.productUrl(p) + '" target="_blank" rel="noopener">See details</a>' +
      '</article>';
    }).join('');

    screen.innerHTML =
      '<div class="dfa-reveal__head">' +
        '<p class="dfa-eyebrow">Duo Gift Arcade</p>' +
        '<h2 class="dfa-title dfa-title--sm">It&#39;s a match!</h2>' +
        '<p class="dfa-reveal__score"><span class="visually-hidden">Score ' + score + '</span>' +
          '<span aria-hidden="true">Score <span data-dfa-count>' + score + '</span></span></p>' +
        starsHtml +
        bestLine +
        '<p class="dfa-reason">' + rec.reason + '</p>' +
      '</div>' +
      '<div class="dfa-picks">' + cards + '</div>' +
      '<p class="dfa-cart-msg" data-dfa-cart-msg aria-live="polite"></p>' +
      '<div class="dac-claim-slot" data-duo-claim-slot></div>' +
      '<div class="dfa-actions">' +
        '<button type="button" class="dfa-btn dfa-btn--ghost" data-dfa-share>Challenge a friend</button>' +
        '<button type="button" class="dfa-btn dfa-btn--ghost" data-dfa-replay>Play again</button>' +
      '</div>';
    this.root.appendChild(screen);

    screen.querySelector('[data-dfa-replay]').addEventListener('click', function () { self.renderIntro(); });
    screen.querySelector('[data-dfa-share]').addEventListener('click', function () { self.shareBoard(this); });
    screen.querySelectorAll('.dfa-pick__add').forEach(function (btn) {
      btn.addEventListener('click', function () { self.addToCart(btn); });
    });

    // Reward claim card (Phase 1B): non-blocking, fails quietly if unavailable.
    if (window.DuoArcadeClaim) {
      window.DuoArcadeClaim.mount({ game: 'fidget', score: this.game.score, slot: screen.querySelector('[data-duo-claim-slot]') });
    }

    // Celebration beats: score rolls up right away, confetti pops as the first
    // cards land. Both are no-ops under reduced motion (score lands instantly).
    this.countUp(screen.querySelector('[data-dfa-count]'), score);
    if (!this.reducedMotion) {
      setTimeout(function () { self.revealBurst(screen); }, 220);
      buzz(15);
    }

    track('reveal_seen', { topCluster: rec.topCluster, picks: rec.picks.length, newBest: isNewBest, stars: stars, mode: this.mode });
    SoundFx.reveal();
  };

  /* ---------- streaks + daily records + share (Lane E) ---------- */

  // Played-days streak (any mode): consecutive local calendar days with at
  // least one finished round. Called once per reveal; idempotent within a day.
  FidgetArcade.prototype.bumpDayStreak = function () {
    var today = localDayStr();
    var yd = new Date(); yd.setDate(yd.getDate() - 1);
    var yesterday = localDayStr(yd);
    var s = readJson(DAYS_KEY) || {};
    if (s.last !== today) s.count = (s.last === yesterday ? (s.count || 0) : 0) + 1;
    s.last = today;
    writeJson(DAYS_KEY, s);
    return s.count || 1;
  };

  // Daily-board record: today's best score + the daily streak. Returns what
  // the reveal needs; only called after a daily round.
  FidgetArcade.prototype.recordDaily = function (score) {
    var today = localDayStr();
    var yd = new Date(); yd.setDate(yd.getDate() - 1);
    var yesterday = localDayStr(yd);
    var d = readJson(DAILY_KEY) || {};
    var prevBest = d.day === today ? (d.best || 0) : 0;
    var newBest = score > prevBest;
    d.day = today;
    d.best = Math.max(prevBest, score);
    if (d.sLast !== today) d.sCount = (d.sLast === yesterday ? (d.sCount || 0) : 0) + 1;
    d.sLast = today;
    writeJson(DAILY_KEY, d);
    return { newBest: newBest, best: d.best, streak: d.sCount || 1 };
  };

  // The deep link that reproduces this exact board: same seed + same grid.
  FidgetArcade.prototype.shareUrl = function () {
    var g = this.game;
    return window.location.origin + window.location.pathname +
      '?game=fidget&seed=' + encodeURIComponent(String(this.seedUsed)) +
      '&grid=' + g.cols + 'x' + g.rows;
  };

  FidgetArcade.prototype.shareBoard = function (btn) {
    var self = this;
    var url = this.shareUrl();
    track('share_clicked', { seed: String(this.seedUsed), score: this.game.score, mode: this.mode });
    function copied() {
      btn.textContent = 'Link copied!';
      btn.classList.add('is-copied');
      setTimeout(function () { btn.textContent = 'Challenge a friend'; btn.classList.remove('is-copied'); }, 2200);
    }
    function fallbackCopy() {
      try {
        var ta = document.createElement('textarea');
        ta.value = url; ta.setAttribute('readonly', '');
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        copied();
      } catch (e) {
        var msg = self.root.querySelector('[data-dfa-cart-msg]');
        if (msg) msg.textContent = 'Copy this link to share your board: ' + url;
      }
    }
    if (navigator.share) {
      navigator.share({
        title: 'Duo Gift Arcade',
        text: 'I scored ' + this.game.score + ' on this fidget board. Beat me!',
        url: url
      }).catch(function () {});
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(copied, fallbackCopy);
    } else fallbackCopy();
  };

  /* ---------- cart ---------- */

  // Cart helpers speak the theme cart drawer's pub-sub contract (cart-drawer.js
  // integration notes) so an add re-renders the drawer/bubble live instead of
  // needing a page reload.
  function cartSurface() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }
  function cartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  }
  function publishCartUpdate(variantId, cartData) {
    if (typeof window.publish !== 'function' || !window.PUB_SUB_EVENTS || !window.PUB_SUB_EVENTS.cartUpdate) {
      return Promise.resolve();
    }
    return window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
      source: 'duo-fidget-arcade',
      productVariantId: String(variantId),
      cartData: cartData
    }).catch(function () {});
  }

  FidgetArcade.prototype.addToCart = function (btn) {
    var self = this;
    var variantId = btn.getAttribute('data-variant');
    var handle = btn.getAttribute('data-handle');
    var msg = this.root.querySelector('[data-dfa-cart-msg]');
    track('cart_clicked', { id: btn.getAttribute('data-id'), variant: variantId });

    // Off-store (local harness / cross-origin preview): open the product page,
    // since /cart/add.js only works same-origin on the live store.
    if (this.storeOrigin) {
      window.open(this.productUrl({ handle: handle, variantId: variantId }), '_blank', 'noopener');
      if (msg) msg.textContent = 'Opened the product page in a new tab.';
      return Promise.resolve();
    }

    btn.disabled = true; btn.classList.add('is-loading');
    if (msg) msg.textContent = 'Adding to cart...';
    var cart = cartSurface();
    var body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map(function (section) { return section.id || section.section; }).filter(Boolean);
      body.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }
    return fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        if (!res.ok || res.j.status) throw new Error(res.j.description || res.j.message || 'Add to cart failed');
        btn.classList.remove('is-loading');
        // success morph: a drawn check + teal state on the button, one pulse on
        // the card. CSS-driven, so reduced motion shows the final state instantly.
        btn.innerHTML = '<svg class="dfa-add-check" viewBox="0 0 16 16" aria-hidden="true">' +
          '<path d="M2.5 8.5l3.5 3.5 7-7.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>Added';
        btn.classList.add('is-added');
        var pickCard = btn.closest('.dfa-pick');
        if (pickCard && !self.reducedMotion) pickCard.classList.add('is-added-pulse');
        buzz(12);
        if (msg) msg.textContent = 'Added to cart.';
        SoundFx.ui();
        track('cart_added', { variant: variantId });
        try { document.dispatchEvent(new CustomEvent('duo:fidget:cart-added', { detail: res.j })); } catch (e) {}
        return publishCartUpdate(variantId, res.j).then(function () {
          if (cart && typeof cart.renderContents === 'function') {
            try {
              cart.renderContents(res.j);
              if (cart.classList && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            } catch (renderError) { console.warn('[duo-fidget-arcade] cart render error', renderError); }
          }
          return res.j;
        });
      }).catch(function (err) {
        console.warn('[duo-fidget-arcade] cart error', err);
        btn.disabled = false; btn.classList.remove('is-loading');
        if (msg) msg.textContent = 'Could not add to cart. ' + (err.message || '');
        throw err;
      });
  };

  /* ---------- test hooks ---------- */

  FidgetArcade.prototype.refreshAll = function () {
    if (!this.tiles) return;
    this.syncAllTiles();
  };

  FidgetArcade.prototype.testApi = function () {
    var self = this;
    return {
      controller: self,
      startWithSeed: function (seed, opts) { self.startGame(seed, opts); return self.game; },
      startDaily: function () { self.startGame(null, { mode: 'daily' }); return self.game; },
      autoPlay: function (n) { E.autoPlay(self.game, n || 40); self.refreshAll(); return { score: self.game.score, tally: self.game.tally }; },
      finishToReveal: function () { self.endGame('test'); return self.rec; },
      soundState: function () { return SoundFx.state(); },
      getState: function () {
        return {
          seed: self.seedUsed, score: self.game ? self.game.score : 0,
          tally: self.game ? self.game.tally : {}, board: self.boardFidgets.map(function (f) { return f.id; }),
          rec: self.rec, mode: self.mode,
          goal: self.goal ? { id: self.goal.f.id, target: self.goal.target, shown: self.goal.shown, done: self.goal.done } : null,
          queued: !!self.queuedMove,
          shareUrl: self.game ? self.shareUrl() : null
        };
      }
    };
  };

  /* ---------- boot ---------- */

  function bootAll() {
    var roots = document.querySelectorAll('[data-duo-fidget-arcade]');
    var instances = [];
    roots.forEach(function (root) {
      if (root.__dfa) return;
      var arcade = new FidgetArcade(root);
      root.__dfa = arcade;
      arcade.boot();
      instances.push(arcade);
    });
    if (instances.length) window.__duoFidgetArcade = instances[0].testApi();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll);
  else bootAll();

  window.DuoFidgetArcade = { boot: bootAll, FidgetArcade: FidgetArcade };
})();
